/**
 * Which columns a view shows, and in what order.
 *
 * **The primary field is not in the list.** It identifies the row, so it is the
 * one column that has to still be there when everything else has scrolled away
 * -- which is why `orderFields` pins it to the front as a rule rather than as a
 * stored position. A control offering to hide it, or to move it, would be
 * offering something the grid would then refuse to do.
 *
 * Reordering hangs off a dedicated handle rather than the row, and so is
 * reachable from the keyboard: the row also holds a switch, and a row that is
 * both a switch and a drag source cannot tell Space-to-toggle from
 * Space-to-pick-up.
 */
import { GripVertical, RectangleEllipsis } from 'lucide-react'
import { verticalListSortingStrategy } from '@dnd-kit/sortable'
import { restrictToVerticalAxis } from '@dnd-kit/modifiers'
import { useTranslation } from 'react-i18next'

import type { Field } from '@/api/types'
import { SortableList } from '@/shared/components/sortable'
import { useSortableItem } from '@/shared/hooks/use-sortable-item'
import { Button } from '@/shared/ui/button'
import { Switch } from '@/shared/ui/switch'
import { Popover, PopoverContent, PopoverTrigger } from '@/shared/ui/popover'
import type { GridFieldOptions } from '../grid/columns'
import { orderFields } from '../views/field-order'
import { useUpdateFieldOptions } from './field-options-mutations'

export function FieldOptionsMenu({
  viewId,
  fields,
  options,
}: {
  viewId: number
  fields: readonly Field[]
  options: Record<string, GridFieldOptions>
}) {
  const { t } = useTranslation()
  const update = useUpdateFieldOptions<GridFieldOptions>(viewId)

  const listed = orderFields(fields, options).filter((field) => !field.primary)
  const hidden = listed.filter((field) => options[String(field.id)]?.hidden === true)

  // No error handling here: a refused change is put back by the mutation and
  // reported by the global handler, in the API's own words rather than in a
  // sentence this file invented for it.
  function change(changes: Record<number, Partial<GridFieldOptions>>): void {
    update.mutate(changes)
  }

  function setAllHidden(value: boolean): void {
    change(Object.fromEntries(listed.map((field) => [field.id, { hidden: value }])))
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="sm" className="gap-1.5">
          <RectangleEllipsis className="size-4 opacity-70" />
          {/* The count rather than the word alone, because a hidden column is
              invisible by definition and the only place it can be reported is
              the control that hid it. */}
          {hidden.length === 0
            ? t('view.fields')
            : t('view.fieldsHidden', { count: hidden.length })}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72 p-2">
        {listed.length === 0 ? (
          <p className="px-2 py-1.5 text-sm text-muted-foreground">
            {t('view.fieldsOnlyPrimary')}
          </p>
        ) : (
          <>
            <ul aria-label={t('view.fields')} className="max-h-72 overflow-y-auto">
              <SortableList
                ids={listed.map((field) => field.id)}
                strategy={verticalListSortingStrategy}
                modifiers={[restrictToVerticalAxis]}
                onReorder={(ids) => {
                  // Numbered from one, leaving zero to the primary field, whose
                  // stored order is never written: its place is decided by the
                  // rule that puts it first, and a number would be a second
                  // answer to a question that already has one.
                  change(
                    Object.fromEntries(
                      ids.map((id, index) => [id, { order: index + 1 }]),
                    ),
                  )
                }}
              >
                {listed.map((field) => (
                  <FieldRow
                    key={field.id}
                    field={field}
                    hidden={options[String(field.id)]?.hidden === true}
                    onToggle={(shown) => {
                      change({ [field.id]: { hidden: !shown } })
                    }}
                  />
                ))}
              </SortableList>
            </ul>
            <div className="mt-2 flex gap-2 border-t pt-2">
              <Button
                variant="outline"
                size="sm"
                className="flex-1"
                onClick={() => {
                  setAllHidden(true)
                }}
              >
                {t('view.hideAll')}
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="flex-1"
                onClick={() => {
                  setAllHidden(false)
                }}
              >
                {t('view.showAll')}
              </Button>
            </div>
          </>
        )}
      </PopoverContent>
    </Popover>
  )
}

function FieldRow({
  field,
  hidden,
  onToggle,
}: {
  field: Field
  hidden: boolean
  onToggle: (shown: boolean) => void
}) {
  const { t } = useTranslation()
  const { setNodeRef, style, handleProps, isDragging } = useSortableItem(field.id)

  return (
    <li
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-2 rounded px-1 py-1 ${isDragging ? 'opacity-60' : ''}`}
    >
      <button
        type="button"
        aria-label={t('view.fieldMove', { field: field.name })}
        className="cursor-grab rounded p-0.5 text-muted-foreground hover:bg-muted"
        {...handleProps}
      >
        <GripVertical className="size-4" />
      </button>
      <Switch
        id={`field-shown-${field.id}`}
        size="sm"
        checked={!hidden}
        onCheckedChange={onToggle}
      />
      <label htmlFor={`field-shown-${field.id}`} className="truncate text-sm">
        {field.name}
      </label>
    </li>
  )
}
